import React, { useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import {
  createPopup,
  deletePopup,
  fetchPopups,
  resolvePopupTargetCsv,
  updatePopup,
  uploadPopupMedia,
} from '../services/popupsApi';
import { searchAppUsers } from '../services/userCrmApi';
import Alert from './Alert';
import Confirm from './Confirm';
import PermissionGate from './PermissionGate';

function toKolkataInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function kolkataInputToIso(value) {
  if (!value) return '';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return '';
  const [, year, month, day, hour, minute] = match.map(Number);
  const utcMs = Date.UTC(year, month - 1, day, hour - 5, minute - 30);
  return new Date(utcMs).toISOString();
}

function formatSchedule(value) {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function isHttpsUrl(value) {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

function normalizeForm(popup) {
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  return {
    title: popup?.title || '',
    type: popup?.type || 'image',
    mediaUrl: popup?.mediaUrl || '',
    bodyText: popup?.bodyText || '',
    ctaLabel: popup?.ctaLabel || 'Update now',
    iosMinimumVersion: popup?.iosMinimumVersion || '',
    androidMinimumVersion: popup?.androidMinimumVersion || '',
    iosUpdateUrl: popup?.iosUpdateUrl || '',
    androidUpdateUrl: popup?.androidUpdateUrl || '',
    forceUpdate: popup?.forceUpdate === true,
    targetUserIds: Array.isArray(popup?.targetUserIds) ? popup.targetUserIds : [],
    startAt: popup?.startAt ? toKolkataInputValue(popup.startAt) : toKolkataInputValue(now.toISOString()),
    endAt: popup?.endAt ? toKolkataInputValue(popup.endAt) : toKolkataInputValue(tomorrow.toISOString()),
    status: popup?.status || 'active',
  };
}

function isValidVersion(value) {
  return /^\d+(?:\.\d+){0,2}$/.test(String(value || '').trim());
}

const MAX_RENDERED_TARGET_CHIPS = 100;
const MAX_UNMATCHED_PHONE_PREVIEW = 10;

function unmatchedPhonePreview(unmatched) {
  const preview = unmatched.slice(0, MAX_UNMATCHED_PHONE_PREVIEW).map((item) => item.input || item.phone);
  const remaining = unmatched.length - preview.length;
  const suffix = remaining > 0 ? ` +${remaining} more` : '';
  return `${preview.join(', ')}${suffix}`;
}

function targetUserCount(popup) {
  return Array.isArray(popup?.targetUserIds) ? popup.targetUserIds.length : 0;
}

function targetUserLabel(user) {
  return user.label || user.fullName || user.displayName || user.phoneNumber || user.email || user.id;
}

function hydrateTargetUsers(targetUserIds) {
  return (Array.isArray(targetUserIds) ? targetUserIds : []).map((id) => ({ id }));
}

function PopupPreview({ form, url }) {
  const { type } = form;
  if (type === 'update') {
    return (
      <div className="bg-gray-50 rounded border p-5">
        <div className="max-w-sm mx-auto bg-white border rounded-lg shadow-sm p-6 text-center">
          <div className="text-lg font-bold text-gray-900">{form.title || 'Update Got Tea'}</div>
          <p className="text-sm text-gray-600 mt-3 whitespace-pre-line">
            {form.bodyText || 'Please update to continue using the app.'}
          </p>
          <button type="button" className="mt-5 w-full bg-teal-500 text-white rounded px-4 py-2 font-semibold">
            {form.ctaLabel || 'Update now'}
          </button>
          {form.forceUpdate && (
            <div className="mt-3 text-xs font-medium text-red-600">Force update: cannot be dismissed</div>
          )}
        </div>
      </div>
    );
  }
  if (!url) {
    return (
      <div className="bg-gray-100 rounded overflow-hidden flex items-center justify-center border text-gray-400 h-52">
        <PhotoIcon className="size-10" />
      </div>
    );
  }
  if (type === 'video') {
    return <video src={url} className="w-full h-72 rounded border bg-black object-contain" controls muted />;
  }
  if (type === 'webview') {
    return <iframe src={url} title="Popup webview preview" className="w-full h-72 rounded border" style={{ border: 'none' }} />;
  }
  return <img src={url} alt="Popup preview" className="w-full max-h-72 rounded border object-contain bg-gray-50" />;
}

function PopupForm({ currentPopup, saving, onCancel, onSubmit }) {
  const [form, setForm] = useState(() => normalizeForm(currentPopup));
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(currentPopup?.mediaUrl || '');
  const [selectedTargetUsers, setSelectedTargetUsers] = useState(() => hydrateTargetUsers(currentPopup?.targetUserIds));
  const [targetSearchQuery, setTargetSearchQuery] = useState('');
  const [targetSearchResults, setTargetSearchResults] = useState([]);
  const [targetSearchLoading, setTargetSearchLoading] = useState(false);
  const [targetSearchError, setTargetSearchError] = useState('');
  const [targetCsvImporting, setTargetCsvImporting] = useState(false);
  const [targetCsvError, setTargetCsvError] = useState('');
  const [targetCsvSummary, setTargetCsvSummary] = useState(null);

  useEffect(() => {
    setForm(normalizeForm(currentPopup));
    setMediaFile(null);
    setMediaPreviewUrl(currentPopup?.mediaUrl || '');
    setSelectedTargetUsers(hydrateTargetUsers(currentPopup?.targetUserIds));
    setTargetSearchQuery('');
    setTargetSearchResults([]);
    setTargetSearchError('');
    setTargetCsvImporting(false);
    setTargetCsvError('');
    setTargetCsvSummary(null);
  }, [currentPopup]);

  useEffect(() => {
    if (!mediaFile) return undefined;
    const url = URL.createObjectURL(mediaFile);
    setMediaPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [mediaFile]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'type') {
        setMediaFile(null);
        setMediaPreviewUrl(value === 'webview' ? next.mediaUrl : currentPopup?.type === value ? currentPopup?.mediaUrl || '' : '');
        if (value !== 'webview') next.mediaUrl = currentPopup?.type === value ? currentPopup?.mediaUrl || '' : '';
        if (value === 'update' && !next.ctaLabel) next.ctaLabel = 'Update now';
      }
      if (field === 'mediaUrl') setMediaPreviewUrl(value);
      return next;
    });
  };

  const handleMediaChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setMediaFile(file);
  };

  const searchTargetUsers = async (event) => {
    event.preventDefault();
    const trimmedQuery = targetSearchQuery.trim();
    if (!trimmedQuery) {
      setTargetSearchError('Enter a name, phone, email, or UID.');
      setTargetSearchResults([]);
      return;
    }

    setTargetSearchLoading(true);
    setTargetSearchError('');
    try {
      const result = await searchAppUsers({
        query: trimmedQuery,
        page: 1,
        pageSize: 8,
        sort: { field: 'updatedAt', direction: 'desc' },
      });
      setTargetSearchResults(result.users || []);
      if (!result.users || result.users.length === 0) {
        setTargetSearchError('No matching users found.');
      }
    } catch (error) {
      setTargetSearchError(error.message || 'Failed to search users.');
      setTargetSearchResults([]);
    } finally {
      setTargetSearchLoading(false);
    }
  };

  const addTargetUser = (user) => {
    setSelectedTargetUsers((prev) => (
      prev.some((item) => item.id === user.id) ? prev : [...prev, user]
    ));
  };

  const importTargetCsv = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;

    setTargetCsvImporting(true);
    setTargetCsvError('');
    setTargetCsvSummary(null);
    try {
      const result = await resolvePopupTargetCsv(file);
      const matched = result.matchedUsers || [];
      const unmatched = result.unmatched || [];
      const stats = result.stats || {};
      const existingIds = new Set(selectedTargetUsers.map((user) => user.id));
      const additions = matched.filter((user) => !existingIds.has(user.id));
      if (additions.length > 0) {
        setSelectedTargetUsers((prev) => {
          const seen = new Set(prev.map((user) => user.id));
          const fresh = additions.filter((user) => !seen.has(user.id));
          return fresh.length > 0 ? [...prev, ...fresh] : prev;
        });
      }
      setTargetCsvSummary({
        added: additions.length,
        matchedCount: matched.length,
        unmatched,
        duplicateCount: stats.duplicateCount || 0,
        invalidCount: stats.invalidCount || 0,
      });
    } catch (error) {
      setTargetCsvError(error.message || 'Failed to import phone numbers from CSV.');
    } finally {
      setTargetCsvImporting(false);
    }
  };

  const removeTargetUser = (userId) => {
    setSelectedTargetUsers((prev) => prev.filter((user) => user.id !== userId));
  };

  const clearTargetUsers = () => {
    setSelectedTargetUsers([]);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const startAt = kolkataInputToIso(form.startAt);
    const endAt = kolkataInputToIso(form.endAt);
    if (!startAt || !endAt) {
      alert('Please enter valid start and end date-times.');
      return;
    }
    if (new Date(endAt).getTime() <= new Date(startAt).getTime()) {
      alert('End date must be after start date.');
      return;
    }
    if (form.type === 'webview' && !isHttpsUrl(form.mediaUrl)) {
      alert('Webview URL must start with https://');
      return;
    }
    if (form.type === 'update') {
      const iosMinimumVersion = form.iosMinimumVersion.trim();
      const androidMinimumVersion = form.androidMinimumVersion.trim();
      if (!form.bodyText.trim()) {
        alert('Update popups need body text.');
        return;
      }
      if (!form.ctaLabel.trim()) {
        alert('Update popups need a CTA label.');
        return;
      }
      if (!iosMinimumVersion && !androidMinimumVersion) {
        alert('Set at least one platform minimum version.');
        return;
      }
      if (iosMinimumVersion && !isValidVersion(iosMinimumVersion)) {
        alert('iOS minimum version must look like 1.0.1.');
        return;
      }
      if (androidMinimumVersion && !isValidVersion(androidMinimumVersion)) {
        alert('Android minimum version must look like 1.0.1.');
        return;
      }
      if (iosMinimumVersion && !isHttpsUrl(form.iosUpdateUrl)) {
        alert('iOS App Store URL must start with https:// when iOS minimum version is set.');
        return;
      }
      if (androidMinimumVersion && !isHttpsUrl(form.androidUpdateUrl)) {
        alert('Android Play Store URL must start with https:// when Android minimum version is set.');
        return;
      }
    }
    if (!currentPopup && form.type !== 'webview' && form.type !== 'update' && !mediaFile) {
      alert('Please choose popup media.');
      return;
    }
    const popup = {
      title: form.title,
      type: form.type,
      mediaUrl: form.type === 'webview' ? form.mediaUrl.trim() : form.mediaUrl,
      targetUserIds: selectedTargetUsers.map((user) => user.id),
      startAt,
      endAt,
      status: form.status,
    };
    if (form.type === 'update') {
      Object.assign(popup, {
        mediaUrl: '',
        bodyText: form.bodyText.trim(),
        ctaLabel: form.ctaLabel.trim(),
        iosMinimumVersion: form.iosMinimumVersion.trim(),
        androidMinimumVersion: form.androidMinimumVersion.trim(),
        iosUpdateUrl: form.iosUpdateUrl.trim(),
        androidUpdateUrl: form.androidUpdateUrl.trim(),
        forceUpdate: form.forceUpdate,
      });
    }
    onSubmit({
      popup,
      mediaFile,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-lg w-full max-w-3xl max-h-svh overflow-y-auto">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-xl font-bold">{currentPopup ? 'Edit Popup' : 'Add Popup'}</h3>
          <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-800">
            <XMarkIcon className="size-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-medium">Title</label>
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Internal title"
                required
              />
            </div>
            <div>
              <label className="block mb-1 font-medium">Content Type</label>
              <select
                className="w-full border px-3 py-2 rounded"
                value={form.type}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <option value="image">Flat image</option>
                <option value="video">Vertical video</option>
                <option value="webview">Frameless webview</option>
                <option value="update">App update</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block mb-1 font-medium">Start date-time</label>
              <input
                type="datetime-local"
                className="w-full border px-3 py-2 rounded"
                value={form.startAt}
                onChange={(e) => handleChange('startAt', e.target.value)}
                required
              />
              <p className="text-xs text-gray-500 mt-1">Asia/Kolkata</p>
            </div>
            <div>
              <label className="block mb-1 font-medium">End date-time</label>
              <input
                type="datetime-local"
                className="w-full border px-3 py-2 rounded"
                value={form.endAt}
                onChange={(e) => handleChange('endAt', e.target.value)}
                required
              />
              <p className="text-xs text-gray-500 mt-1">Asia/Kolkata</p>
            </div>
            <div>
              <label className="block mb-1 font-medium">Status</label>
              <select
                className="w-full border px-3 py-2 rounded"
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="border rounded p-4 space-y-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <div className="font-medium">Target users</div>
                <p className="text-sm text-gray-500">
                  {selectedTargetUsers.length === 0
                    ? 'Global popup: visible to all eligible signed-in app users.'
                    : `Targeted popup: visible to ${selectedTargetUsers.length} selected user${selectedTargetUsers.length === 1 ? '' : 's'}.`}
                </p>
              </div>
              {selectedTargetUsers.length > 0 && (
                <button type="button" onClick={clearTargetUsers} className="text-sm text-red-600 hover:opacity-70">
                  Make global
                </button>
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-2">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="size-5 text-gray-400 absolute left-3 top-2.5" />
                <input
                  type="search"
                  className="w-full border pl-10 pr-3 py-2 rounded"
                  value={targetSearchQuery}
                  onChange={(e) => setTargetSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') searchTargetUsers(e);
                  }}
                  placeholder="Search by name, phone, email, or UID"
                />
              </div>
              <button
                type="button"
                onClick={searchTargetUsers}
                className="border px-4 py-2 rounded hover:bg-gray-50 disabled:opacity-60"
                disabled={targetSearchLoading}
              >
                {targetSearchLoading ? 'Searching...' : 'Search users'}
              </button>
            </div>

            {targetSearchError && <div className="text-sm text-red-600">{targetSearchError}</div>}

            <div className="flex flex-col md:flex-row md:items-center gap-2">
              <label className="border px-4 py-2 rounded hover:bg-gray-50 cursor-pointer text-center shrink-0">
                <input
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  className="hidden"
                  onChange={importTargetCsv}
                  disabled={targetCsvImporting}
                />
                {targetCsvImporting ? 'Importing...' : 'Upload phone CSV'}
              </label>
              <p className="text-sm text-gray-500">
                Add many users at once with a CSV of phone numbers — a phone column is detected automatically.
              </p>
            </div>

            {targetCsvError && <div className="text-sm text-red-600">{targetCsvError}</div>}

            {targetCsvSummary && (
              <div className="text-sm">
                {targetCsvSummary.added > 0 ? (
                  <div className="text-teal-700">
                    Added {targetCsvSummary.added} user{targetCsvSummary.added === 1 ? '' : 's'} from CSV.
                  </div>
                ) : (
                  <div className="text-gray-600">No new users added from CSV.</div>
                )}
                {targetCsvSummary.matchedCount > targetCsvSummary.added && (
                  <div className="text-gray-500">
                    {targetCsvSummary.matchedCount - targetCsvSummary.added} matched user
                    {targetCsvSummary.matchedCount - targetCsvSummary.added === 1 ? ' was' : 's were'} already targeted.
                  </div>
                )}
                {targetCsvSummary.duplicateCount > 0 && (
                  <div className="text-gray-500">{targetCsvSummary.duplicateCount} duplicate number{targetCsvSummary.duplicateCount === 1 ? '' : 's'} skipped.</div>
                )}
                {targetCsvSummary.invalidCount > 0 && (
                  <div className="text-gray-500">{targetCsvSummary.invalidCount} invalid number{targetCsvSummary.invalidCount === 1 ? '' : 's'} skipped.</div>
                )}
                {targetCsvSummary.unmatched.length > 0 && (
                  <div className="text-amber-700 mt-1">
                    {targetCsvSummary.unmatched.length} number{targetCsvSummary.unmatched.length === 1 ? ' has' : 's have'} no matching account:
                    <span className="break-all"> {unmatchedPhonePreview(targetCsvSummary.unmatched)}</span>
                  </div>
                )}
              </div>
            )}

            {targetSearchResults.length > 0 && (
              <div className="border rounded divide-y max-h-52 overflow-y-auto">
                {targetSearchResults.map((user) => {
                  const selected = selectedTargetUsers.some((item) => item.id === user.id);
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => addTargetUser(user)}
                      className="w-full text-left px-3 py-2 hover:bg-teal-50 disabled:bg-gray-50 disabled:text-gray-400"
                      disabled={selected}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium">{targetUserLabel(user)}</div>
                          <div className="text-xs text-gray-500 break-all">{user.id}</div>
                        </div>
                        <span className="text-sm text-teal-600">{selected ? 'Added' : 'Add'}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {selectedTargetUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedTargetUsers.slice(0, MAX_RENDERED_TARGET_CHIPS).map((user) => (
                  <div key={user.id} className="flex items-center gap-2 border rounded-full px-3 py-1 bg-gray-50 max-w-full">
                    <span className="text-sm truncate">
                      {targetUserLabel(user)}
                      <span className="text-gray-500"> · {user.id}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeTargetUser(user.id)}
                      className="text-gray-500 hover:text-red-600 shrink-0"
                      aria-label={`Remove ${user.id}`}
                    >
                      <XMarkIcon className="size-4" />
                    </button>
                  </div>
                ))}
                {selectedTargetUsers.length > MAX_RENDERED_TARGET_CHIPS && (
                  <div className="flex items-center border rounded-full px-3 py-1 bg-gray-50 text-sm text-gray-500">
                    +{selectedTargetUsers.length - MAX_RENDERED_TARGET_CHIPS} more
                  </div>
                )}
              </div>
            )}
          </div>

          {form.type === 'update' ? (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 font-medium">CTA label</label>
                  <input
                    type="text"
                    className="w-full border px-3 py-2 rounded"
                    value={form.ctaLabel}
                    onChange={(e) => handleChange('ctaLabel', e.target.value)}
                    placeholder="Update now"
                    required
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 border rounded px-3 py-2 w-full min-h-10">
                    <input
                      type="checkbox"
                      checked={form.forceUpdate}
                      onChange={(e) => handleChange('forceUpdate', e.target.checked)}
                    />
                    <span className="font-medium">Force update</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block mb-1 font-medium">Body text</label>
                <textarea
                  className="w-full border px-3 py-2 rounded min-h-24"
                  value={form.bodyText}
                  onChange={(e) => handleChange('bodyText', e.target.value)}
                  placeholder="Please update to continue using the app."
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 font-medium">iOS minimum version</label>
                  <input
                    type="text"
                    className="w-full border px-3 py-2 rounded"
                    value={form.iosMinimumVersion}
                    onChange={(e) => handleChange('iosMinimumVersion', e.target.value)}
                    placeholder="1.0.1"
                  />
                </div>
                <div>
                  <label className="block mb-1 font-medium">Android minimum version</label>
                  <input
                    type="text"
                    className="w-full border px-3 py-2 rounded"
                    value={form.androidMinimumVersion}
                    onChange={(e) => handleChange('androidMinimumVersion', e.target.value)}
                    placeholder="1.0.1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 font-medium">iOS App Store URL</label>
                  <input
                    type="url"
                    className="w-full border px-3 py-2 rounded"
                    value={form.iosUpdateUrl}
                    onChange={(e) => handleChange('iosUpdateUrl', e.target.value)}
                    placeholder="https://apps.apple.com/..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Required when iOS minimum version is set.</p>
                </div>
                <div>
                  <label className="block mb-1 font-medium">Android Play Store URL</label>
                  <input
                    type="url"
                    className="w-full border px-3 py-2 rounded"
                    value={form.androidUpdateUrl}
                    onChange={(e) => handleChange('androidUpdateUrl', e.target.value)}
                    placeholder="https://play.google.com/store/apps/details?id=in.gottea.app"
                  />
                  <p className="text-xs text-gray-500 mt-1">Required when Android minimum version is set.</p>
                </div>
              </div>
            </div>
          ) : form.type === 'webview' ? (
            <div>
              <label className="block mb-1 font-medium">Webview URL</label>
              <input
                type="url"
                className="w-full border px-3 py-2 rounded"
                value={form.mediaUrl}
                onChange={(e) => handleChange('mediaUrl', e.target.value)}
                placeholder="https://..."
                required
              />
            </div>
          ) : (
            <div>
              <label className="block mb-1 font-medium">{form.type === 'video' ? 'Vertical video' : 'Popup image'}</label>
              <input
                type="file"
                accept={form.type === 'video' ? 'video/*' : 'image/*'}
                className="w-full border px-3 py-2 rounded"
                onChange={handleMediaChange}
              />
              <p className="text-sm text-gray-500 mt-2">Media is uploaded when you save the popup.</p>
              {mediaFile && <p className="text-sm text-teal-600 mt-1">{mediaFile.name}</p>}
            </div>
          )}

          <PopupPreview form={form} url={mediaPreviewUrl || form.mediaUrl} />

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onCancel} className="border px-4 py-2 rounded hover:bg-gray-50" disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="bg-teal-500 text-white px-4 py-2 rounded disabled:opacity-60" disabled={saving}>
              {saving ? 'Saving...' : mediaFile ? 'Save Popup & Media' : 'Save Popup'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TypePreview({ popup }) {
  if (popup.type === 'video') {
    return popup.mediaUrl ? <video src={popup.mediaUrl} className="w-20 h-28 object-cover rounded bg-black" muted /> : <PhotoIcon className="size-7 text-gray-400" />;
  }
  if (popup.type === 'webview') {
    return <div className="w-20 h-28 rounded bg-gray-100 border flex items-center justify-center text-xs text-gray-500">Web</div>;
  }
  if (popup.type === 'update') {
    return (
      <div className="w-20 h-28 rounded bg-teal-50 border border-teal-100 flex flex-col items-center justify-center text-xs text-teal-700 font-semibold">
        <ArrowPathIcon className="size-6 mb-1" />
        Update
      </div>
    );
  }
  return popup.mediaUrl ? <img src={popup.mediaUrl} alt={popup.title} className="w-20 h-28 object-cover rounded bg-gray-100" /> : <PhotoIcon className="size-7 text-gray-400" />;
}

function popupDetailText(popup) {
  if (popup.type === 'update') {
    const versions = [
      popup.iosMinimumVersion ? `iOS ${popup.iosMinimumVersion}` : null,
      popup.androidMinimumVersion ? `Android ${popup.androidMinimumVersion}` : null,
    ].filter(Boolean).join(' · ');
    return versions || 'No version gates set';
  }
  return popup.mediaUrl || 'No media yet';
}

function popupTargetText(popup) {
  const count = targetUserCount(popup);
  return count > 0 ? `Targeted: ${count} user${count === 1 ? '' : 's'}` : 'Global';
}

function Popups() {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [currentPopup, setCurrentPopup] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadPopups = async () => {
    setLoading(true);
    try {
      setPopups(await fetchPopups());
    } catch (e) {
      setAlertMessage(e.message || 'Failed to load popups.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPopups(); }, []);

  const startCreate = () => { setCurrentPopup(null); setShowForm(true); };
  const startEdit = (popup) => { setCurrentPopup(popup); setShowForm(true); };

  const handleSubmit = async ({ popup: payload, mediaFile }) => {
    setSaving(true);
    try {
      let saved;
      if (currentPopup) {
        saved = await updatePopup(currentPopup.id, payload);
      } else {
        saved = await createPopup(payload);
      }
      if (mediaFile) {
        await uploadPopupMedia(saved.id, mediaFile);
      }
      setAlertMessage(mediaFile ? 'Popup and media saved.' : 'Popup saved.');
      setShowForm(false);
      setCurrentPopup(null);
      await loadPopups();
    } catch (e) {
      setAlertMessage(e.message || 'Failed to save popup.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (popup) => {
    setConfirmMessage(`Delete popup "${popup.title || popup.id}"? This cannot be undone.`);
    setConfirmCallback(() => async () => {
      try {
        await deletePopup(popup.id);
        setConfirmMessage(null);
        setAlertMessage('Popup deleted.');
        await loadPopups();
      } catch (e) {
        setAlertMessage(e.message || 'Failed to delete popup.');
      }
    });
  };

  return (
    <div className="m-auto p-6 md:p-10 max-w-screen-xl">
      {alertMessage && <Alert message={alertMessage} onClose={() => setAlertMessage(null)} />}
      {confirmMessage && (
        <Confirm
          message={confirmMessage}
          onConfirm={confirmCallback}
          onCancel={() => setConfirmMessage(null)}
        />
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Popups</h2>
          <p className="text-gray-500 mt-1">Manage scheduled homepage popup campaigns.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={loadPopups} className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50">
            <ArrowPathIcon className="size-5" />
            Refresh
          </button>
          <PermissionGate permission="content.popups.create"><button type="button" onClick={startCreate} className="bg-teal-500 text-white px-4 py-2 rounded flex items-center gap-2">
            <PlusIcon className="size-5" />
            Add Popup
          </button></PermissionGate>
        </div>
      </div>

      {showForm && (
        <PopupForm
          currentPopup={currentPopup}
          saving={saving}
          onCancel={() => setShowForm(false)}
          onSubmit={handleSubmit}
        />
      )}

      {loading ? (
        <div className="text-center py-10">
          <div className="loader border-t-4 border-teal-500 border-solid rounded-full w-12 h-12 mx-auto animate-spin"></div>
          <p className="mt-2">Loading popups...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white shadow rounded text-left">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">Popup</th>
                <th className="py-2 px-4 border-b">Type</th>
                <th className="py-2 px-4 border-b hidden lg:table-cell">Schedule</th>
                <th className="py-2 px-4 border-b hidden md:table-cell">Status</th>
                <th className="py-2 px-4 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {popups.map((popup) => (
                <tr key={popup.id}>
                  <td className="py-3 px-4 border-b">
                    <div className="flex items-center gap-3 min-w-64">
                      <TypePreview popup={popup} />
                      <div>
                        <div className="font-semibold">{popup.title || <span className="text-gray-400">Untitled</span>}</div>
                        <div className="text-xs text-gray-500 break-all max-w-sm">{popupDetailText(popup)}</div>
                        {popup.type === 'update' && popup.forceUpdate && (
                          <div className="text-xs font-medium text-red-600 mt-1">Force update</div>
                        )}
                        <div className={`text-xs font-medium mt-1 ${targetUserCount(popup) > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
                          {popupTargetText(popup)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 border-b capitalize">{popup.type}</td>
                  <td className="py-3 px-4 border-b hidden lg:table-cell">
                    <div className="text-sm">Starts {formatSchedule(popup.startAt)}</div>
                    <div className="text-sm text-gray-500">Ends {formatSchedule(popup.endAt)}</div>
                  </td>
                  <td className="py-3 px-4 border-b hidden md:table-cell">
                    <span className={`px-2 py-1 rounded text-sm ${
                      popup.status === 'active' ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {popup.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 border-b">
                    <div className="flex items-center gap-2">
                      <PermissionGate permission="content.popups.update"><button type="button" onClick={() => startEdit(popup)} className="text-teal-500 hover:opacity-70">
                        <PencilSquareIcon className="size-7" />
                      </button></PermissionGate>
                      <PermissionGate permission="content.popups.delete"><button type="button" onClick={() => handleDelete(popup)} className="text-red-500 hover:opacity-70">
                        <TrashIcon className="size-7" />
                      </button></PermissionGate>
                    </div>
                  </td>
                </tr>
              ))}
              {popups.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-8">No popups yet. Add one to get started.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Popups;
